/**
 * Marketing tracking layer
 * ------------------------
 * Captures one persistent browser session id and the first-touch UTM
 * parameters so that an anonymous visitor can be attributed back to their
 * acquisition source when they later sign up.
 *
 * Three concerns live here:
 *   1. Stable session id (localStorage, survives reloads)
 *   2. First-touch session entry — written ONCE per session with UTMs +
 *      landing page + referrer. UTMs are locked at the DB layer (no UPDATE
 *      policy from clients) so they cannot be overwritten later.
 *   3. Signup attribution — RPC `claim_signup_attribution` copies the
 *      session UTMs onto the new user's profile atomically.
 *
 * All calls are fire-and-forget. Analytics must never break UX.
 */
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "sorvaine_cta_session_id";
const ENTRY_RECORDED_KEY = "sorvaine_session_entry_recorded";

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

type UtmKey = (typeof UTM_PARAMS)[number];

/**
 * Routes that belong to the authenticated app or admin surface. Tracking
 * these would pollute marketing analytics with internal traffic, so all
 * pageview / session / CTA writes are skipped when the current path starts
 * with one of these prefixes.
 */
const NON_MARKETING_PREFIXES = [
  "/admin",
  "/dashboard",
  "/proposals",
  "/settings",
  "/auth",
  "/signup",
  "/signin",
  "/login",
  "/onboarding",
  "/iterate",
  "/library",
  "/knowledge-vault",
  "/account",
  "/billing",
] as const;

export function isMarketingPath(path: string | null | undefined): boolean {
  if (!path) return false;
  for (const p of NON_MARKETING_PREFIXES) {
    if (path === p || path.startsWith(p + "/")) return false;
  }
  return true;
}


/** Stable per-browser session id. Survives reloads, persists until cleared. */
export function getMarketingSessionId(): string {
  if (typeof window === "undefined") return "ssr-session";
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        crypto?.randomUUID?.() ??
        `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

function readUtmsFromUrl(): Partial<Record<UtmKey | "gclid", string>> {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const out: Partial<Record<UtmKey | "gclid", string>> = {};
    for (const k of UTM_PARAMS) {
      const v = params.get(k);
      if (v) out[k] = v.slice(0, 200);
    }
    const g = params.get("gclid");
    if (g) out.gclid = g.slice(0, 200);
    return out;
  } catch {
    return {};
  }
}

function detectDeviceClass(): string {
  if (typeof window === "undefined") return "unknown";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

/**
 * Record the session entry exactly once per browser session.
 * The DB INSERT will fail silently on conflict (session_id is PK) — that's
 * the desired "lock UTMs at first touch" behaviour.
 */
export async function recordSessionEntryOnce(): Promise<void> {
  if (typeof window === "undefined") return;
  if (!isMarketingPath(window.location.pathname)) return;
  try {
    if (sessionStorage.getItem(ENTRY_RECORDED_KEY) === "1") return;
    sessionStorage.setItem(ENTRY_RECORDED_KEY, "1");


    const session_id = getMarketingSessionId();
    const utms = readUtmsFromUrl();

    await supabase.functions.invoke("record-marketing-session", {
      body: {
        session_id,
        first_landing_path: window.location.pathname.slice(0, 500),
        first_referrer: document.referrer ? document.referrer.slice(0, 1000) : null,
        utm_source: utms.utm_source ?? null,
        utm_medium: utms.utm_medium ?? null,
        utm_campaign: utms.utm_campaign ?? null,
        utm_term: utms.utm_term ?? null,
        utm_content: utms.utm_content ?? null,
        gclid: utms.gclid ?? null,
        device_class: detectDeviceClass(),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        user_agent: navigator.userAgent ? navigator.userAgent.slice(0, 1000) : null,
      },
    });
  } catch {
    // Best-effort. Conflict on PK or RLS rejection are both fine.
  }
}

/**
 * Insert a pageview. Returns the row id so the caller can later UPDATE
 * dwell_ms / max_scroll_pct on unmount.
 */
export async function recordPageview(path: string, referrer: string | null): Promise<string | null> {
  try {
    if (!isMarketingPath(path)) return null;
    const session_id = getMarketingSessionId();

    const { data: userData } = await supabase.auth.getUser();
    const user_id = userData?.user?.id ?? null;

    const { data, error } = await supabase
      .from("pageview_events")
      .insert({
        session_id,
        user_id,
        path: path.slice(0, 500),
        referrer: referrer ? referrer.slice(0, 1000) : null,
      })
      .select("id")
      .single();

    if (error) return null;
    return data?.id ?? null;
  } catch {
    return null;
  }
}

/** Backfill dwell + scroll onto a previously-inserted pageview row. */
export async function updatePageviewMetrics(
  rowId: string,
  dwellMs: number,
  maxScrollPct: number,
): Promise<void> {
  if (!rowId) return;
  try {
    await supabase
      .from("pageview_events")
      .update({
        dwell_ms: Math.max(0, Math.min(7200000, Math.round(dwellMs))),
        max_scroll_pct: Math.max(0, Math.min(100, Math.round(maxScrollPct))),
      })
      .eq("id", rowId);
  } catch {
    // best effort
  }
}

/**
 * Called once a user is authenticated (signup OR signin). Idempotent: the
 * underlying RPC uses COALESCE so re-runs never overwrite existing values.
 * This is the join between anonymous marketing traffic and real accounts.
 */
export async function claimSignupAttribution(): Promise<void> {
  try {
    const session_id = getMarketingSessionId();
    if (!session_id || session_id === "ssr-session") return;
    await supabase.rpc("claim_signup_attribution", { p_session_id: session_id });
  } catch {
    // best effort
  }
}
